import React, { useState, useEffect, useCallback } from 'react';
import { 
  Alert, Paper, Stack, TextField, Button, Typography, Slider, Switch, 
  FormControlLabel, Divider, Box, Grid, Card, CardContent, Chip,
  List, ListItem, ListItemText, ListItemIcon, Avatar, Drawer, IconButton,
  MenuItem
} from '@mui/material';
import { 
  Science as ScienceIcon, 
  Settings as SettingsIcon, 
  Description as DescriptionIcon,
  PlayArrow as PlayArrowIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Close as CloseIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { API } from '../../lib/api';
import useStudioStore from '../../state/studioStore';

export default function Studio() {
  const { currentRun, pushEvent } = useStudioStore();
  const [selectedModel, setSelectedModel] = useState('auto');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // Model options for dropdown
  const modelOptions = [
    { value: 'auto', label: 'Auto (Best Available)' },
    { value: 'grok', label: 'Grok (xAI)' },
    { value: 'gemini', label: 'Gemini (Google)' },
    { value: 'openai', label: 'OpenAI (GPT-4)' },
    { value: 'claude', label: 'Claude (Anthropic)' }
  ];
  // Search handler
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchResults(null);
    try {
      const response = await API.rag.search({
        query: searchQuery,
        model: selectedModel !== 'auto' ? selectedModel : undefined,
        limit: 3,
        collection: docCollection
      });
      setSearchResults(response);
    } catch (err) {
      setSearchResults({ error: err.message || 'Search failed.' });
    } finally {
      setSearchLoading(false);
    }
  };
  const [suiteId, setSuiteId] = useState(null);
  const [existingSuites, setExistingSuites] = useState([]);
  const [suiteJson, setSuiteJson] = useState('{\n  "name": "",\n  "cases": []\n}');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Local state for seamless experience  
  const [recentRuns, setRecentRuns] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [currentResults, setCurrentResults] = useState(null);

  const [chunk, setChunk] = useState(800);
  const [overlap, setOverlap] = useState(120);
  const [hybrid, setHybrid] = useState(true);
  const [lambda, setLambda] = useState(0.4);
  const [k, setK] = useState(8);
  const [reranker, setReranker] = useState('none');

  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState([]);

  const [documents, setDocuments] = useState([]);
  const [docCollection, setDocCollection] = useState('default');
  const [docTitle, setDocTitle] = useState('');
  const [docContent, setDocContent] = useState('');
  const [docMetadata, setDocMetadata] = useState('');
  const [docError, setDocError] = useState('');
  const [docMessage, setDocMessage] = useState('');
  const [docLoading, setDocLoading] = useState(false);
  const [docLoadingList, setDocLoadingList] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [docPage, setDocPage] = useState(0);
  const [docLimit, setDocLimit] = useState(10);
  const [docTotal, setDocTotal] = useState(0);

  const ensureCollection = useCallback((value) => {
    const trimmed = (value || 'default').trim();
    return trimmed.length === 0 ? 'default' : trimmed;
  }, []);

  const toDate = (value) => {
    if (!value) return null;
    if (typeof value === 'number' && Number.isFinite(value)) {
      const numeric = value > 1e12 ? value : value * 1000;
      const d = new Date(numeric);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDateTime = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleString() : 'Unknown';
  };

  const formatTime = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleTimeString() : 'Unknown';
  };

  const formatPercent = (value, digits = 0) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? '—' : (numeric * 100).toFixed(digits);
  };

  const classifyPassRate = (value) => {
    if (typeof value !== 'number') {
      return 'default';
    }
    if (value > 0.8) return 'success';
    if (value > 0.5) return 'warning';
    return 'error';
  };

  const loadDocuments = useCallback(async ({ collection, page, search } = {}) => {
    const targetCollection = ensureCollection(collection ?? docCollection);
    const targetPage = typeof page === 'number' ? page : docPage;
    const searchTerm = (search ?? docSearch).trim();

    setDocError('');
    setDocLoadingList(true);

    try {
      const offset = targetPage * docLimit;
      const response = await API.rag.listDocuments({
        collection: targetCollection,
        limit: docLimit,
        offset,
        search: searchTerm ? searchTerm : undefined,
      });

      const items = Array.isArray(response?.items) ? response.items : [];
      const total = response?.pagination?.total ?? items.length;

      setDocuments(items);
      setDocTotal(total);
    } catch (err) {
      if (err?.status === 404) {
        setDocuments([]);
        setDocTotal(0);
        return;
      }
      console.error('RAG Studio document load failed:', err);
      setDocError(err.message || 'Failed to load documents');
      setDocuments([]);
      setDocTotal(0);
    } finally {
      setDocLoadingList(false);
    }
  }, [docCollection, docPage, docSearch, docLimit, ensureCollection]);

  const addDocument = useCallback(async () => {
    const collection = ensureCollection(docCollection);
    if (!docContent.trim()) {
      setDocError('Content is required');
      return;
    }

    setDocError('');
    setDocMessage('');
    setDocLoading(true);

    try {
      let metadata = undefined;
      if (docMetadata.trim()) {
        try {
          metadata = JSON.parse(docMetadata);
        } catch (parseErr) {
          setDocError('Metadata must be valid JSON');
          return;
        }
      }

      const payload = {
        collection,
        title: docTitle.trim() || null,
        content: docContent,
        metadata,
      };

      const saved = await API.rag.createDocument(payload);
      setDocMessage('Document stored.');
      setDocTitle('');
      setDocContent('');
      setDocPage(0);
      loadDocuments({ collection, page: 0, search: docSearch });
      pushEvent({
        type: 'rag-document',
        title: `Stored document in ${collection}`,
        meta: { collection, documentId: saved?.id },
        source: 'studio'
      });
    } catch (err) {
      console.error('Failed to store document:', err);
      setDocError(err.message || 'Failed to store document');
    } finally {
      setDocLoading(false);
    }
  }, [docCollection, docTitle, docContent, docMetadata, docSearch, loadDocuments, pushEvent]);

  const handleDeleteDocument = useCallback(async (documentId) => {
    if (!documentId) {
      return;
    }

    setDocError('');
    setDocMessage('');

    try {
      await API.rag.deleteDocument(documentId);
      setDocMessage('Document deleted.');

      const newTotal = Math.max(docTotal - 1, 0);
      const maxPage = Math.max(Math.ceil(newTotal / docLimit) - 1, 0);
      const targetPage = Math.min(docPage, maxPage);
      setDocPage(targetPage);
      await loadDocuments({ page: targetPage, search: docSearch });

      pushEvent({
        type: 'rag-document',
        title: 'Document removed',
        meta: { documentId },
        source: 'studio'
      });
    } catch (err) {
      console.error('Failed to delete document:', err);
      setDocError(err.message || 'Failed to delete document');
    }
  }, [docLimit, docPage, docSearch, docTotal, loadDocuments, pushEvent]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      
      try {
        console.log('🧪 RAG Studio: Loading suites and runs from database...');
        
        // Load existing suites
        const suitesResponse = await API.proof.suites();
        const suitesData = Array.isArray(suitesResponse) ? suitesResponse : [];
        console.log('Loaded', suitesData.length, 'suites:', suitesData.map(s => s.name));
        setExistingSuites(suitesData);
        
        // Load recent runs
        const runsResponse = await API.proof.runs();
        const runsData = Array.isArray(runsResponse) ? runsResponse : [];
        console.log('Loaded', runsData.length, 'runs');
        const recentRunsList = runsData.slice(-10).reverse();
        setRuns(runsData.slice(-6).reverse());
        setRecentRuns(recentRunsList);
        
        // If we have suites, auto-select the first one
        if (suitesData.length > 0) {
          setSuiteId(suitesData[0].id);
          setSuiteJson(JSON.stringify(suitesData[0], null, 2));
        }
        
      } catch (err) {
        console.error('RAG Studio data load failed:', err);
        setError('Failed to load data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    setDocPage(0);
  }, [docCollection, docSearch]);

  useEffect(() => {
    loadDocuments({ page: docPage });
  }, [docPage, docCollection, docSearch, docLimit, loadDocuments]);

  const loadRunDetails = async (runId) => {
    try {
      const run = recentRuns.find(r => r.id === runId);
      if (run) {
        setSelectedRun(run);
        setAnalysisOpen(true);
      }
    } catch (err) {
      console.error('Failed to load run details:', err);
    }
  };

  const createSuite = async () => {
    try {
      const suite = JSON.parse(suiteJson);
      const result = await API.proof.createSuite(suite);
      setSuiteId(result.id);
      return result;
    } catch (err) {
      console.error('Failed to create suite:', err);
      throw err;
    }
  };

  const runProof = async () => {
    try {
      if (!suiteId) await createSuite();
      const config = {
        name: "exp-1", 
        chunk_size: chunk, 
        overlap, 
        hybrid,
        mmr_lambda: lambda, 
        k, 
        reranker
      };
      const result = await API.proof.createRun(suiteId, config);
      setRun(result);
      
      const allRuns = await API.proof.runs();
      setRuns(allRuns.slice(-6).reverse());
    } catch (err) {
      console.error('Failed to run proof:', err);
    }
  };

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden' }}>
      {/* Enhanced Header with Context */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ScienceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>RAG Quality Studio</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Experiment with chunking, MMR, hybrid search - see real-time metrics
              </Typography>
            </Box>
          </Stack>
          
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryOpen(true)}
              variant="outlined"
              size="small"
            >
              Recent Runs ({recentRuns.length})
            </Button>
            <Button
              startIcon={<AnalyticsIcon />}
              onClick={() => setAnalysisOpen(true)}
              variant="outlined"
              size="small"

            >
              Analysis Tools
            </Button>
          </Stack>
        </Stack>
        
        {/* Quick Context Bar */}
        {recentRuns.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ opacity: 0.6, mb: 1, display: 'block' }}>
              Recent Activity
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {recentRuns.slice(0, 3).map((run) => {
                const passRateValue = typeof run?.metrics?.pass_rate === 'number' ? run.metrics.pass_rate : null;
                const passRateLabel = passRateValue === null ? '—' : `${formatPercent(passRateValue, 0)}%`;
                const latencyLabel = run?.metrics?.avg_latency_ms ?? '—';
                return (
                  <Chip
                    key={run.id}
                    label={`${passRateLabel} pass • ${latencyLabel}ms`}
                    size="small"
                    onClick={() => loadRunDetails(run.id)}
                    color={classifyPassRate(passRateValue)}
                    variant="outlined"
                  />
                );
              })}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Dual Panel Layout */}
      <Grid container sx={{ height: 'calc(100vh - 120px)' }} spacing={2}>
        {/* Left Panel - Controls */}
        <Grid item xs={12} md={5} sx={{ height: '100%', overflow: 'auto' }}>
          <Stack spacing={3} sx={{ pr: 1 }}>
            {/* AI Model Selection & Search */}
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <InsightsIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>AI Model & Search</Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  label="Query"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="AI Model"
                  select
                  value={selectedModel}
                  onChange={e => setSelectedModel(e.target.value)}
                  size="small"
                  fullWidth
                >
                  {modelOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searchLoading || !searchQuery.trim()}
                >
                  {searchLoading ? 'Searching…' : 'Search & Generate Answer'}
                </Button>
              </Stack>
              {searchResults && (
                <Box sx={{ mt: 3 }}>
                  {searchResults.error ? (
                    <Alert severity="error">{searchResults.error}</Alert>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        <Chip label={`Model: ${searchResults.model || 'auto'}`} color="primary" size="small" />
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        <b>AI Answer:</b> {searchResults.answer}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Top Retrieved Documents:</Typography>
                      <Stack spacing={1}>
                        {(searchResults.results || []).map((doc, idx) => (
                          <Paper key={doc.id || idx} sx={{ p: 2, bgcolor: 'background.paper' }}>
                            <Typography variant="subtitle2">{doc.title || 'Untitled'}</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>{doc.content.slice(0, 180)}{doc.content.length > 180 ? '…' : ''}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Score: {doc.score} • Distance: {doc.distance}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              )}
            </Paper>
            {/* Suite Configuration */}
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <DescriptionIcon sx={{ color: 'info.main' }} />
                <Typography variant="h6" fontWeight={600}>Eval Suite</Typography>
              </Stack>
              
              {error && (
                <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>
                  {error}
                </Typography>
              )}
              
              {existingSuites.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Database Suites ({existingSuites.length}):
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {existingSuites.map(suite => (
                      <Chip
                        key={suite.id}
                        label={`${suite.name} (${suite.cases?.length || 0} cases)`}
                        onClick={() => {
                          setSuiteId(suite.id);
                          setSuiteJson(JSON.stringify(suite, null, 2));
                        }}
                        color={suiteId === suite.id ? 'primary' : 'default'}
                        variant={suiteId === suite.id ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                {existingSuites.length > 0 ? 'Select a suite above or create custom JSON below' : 'Define test cases with questions and expected citations'}
              </Typography>
              
              <TextField 
                multiline 
                minRows={8} 
                fullWidth 
                value={suiteJson} 
                onChange={e=>setSuiteJson(e.target.value)}
                placeholder="Enter your evaluation suite JSON..."
                sx={{ mb: 2 }}
              />
              <Button 
                variant="outlined" 
                onClick={createSuite}
                startIcon={<PlayArrowIcon />}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Create New Suite'}
              </Button>
              {suiteId && (
                <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                  Suite Selected: <b>{suiteId}</b>
                </Typography>
              )}
            </Paper>

            {/* Retrieval Configuration */}
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SettingsIcon sx={{ color: 'warning.main' }} />
                <Typography variant="h6" fontWeight={600}>Retrieval Config</Typography>
              </Stack>
              
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>Chunk Size: {chunk}</Typography>
                  <Slider value={chunk} min={200} max={1800} step={50} onChange={(_,v)=>setChunk(v)} />
                </Box>
                
                <Box>
                  <Typography variant="body2" fontWeight={500}>Overlap: {overlap}</Typography>
                  <Slider value={overlap} min={0} max={400} step={10} onChange={(_,v)=>setOverlap(v)} />
                </Box>
                
                <FormControlLabel 
                  control={<Switch checked={hybrid} onChange={e=>setHybrid(e.target.checked)} />} 
                  label="Hybrid Search (BM25 + dense)"
                />
                
                <Box>
                  <Typography variant="body2" fontWeight={500}>MMR Lambda: {lambda.toFixed(2)}</Typography>
                  <Slider value={lambda} min={0} max={1} step={0.05} onChange={(_,v)=>setLambda(v)} />
                </Box>
                
                <Box>
                  <Typography variant="body2" fontWeight={500}>Top-k: {k}</Typography>
                  <Slider value={k} min={3} max={20} step={1} onChange={(_,v)=>setK(v)} />
                </Box>
                
                <FormControlLabel 
                  control={<Switch checked={reranker !== 'none'} onChange={e=>setReranker(e.target.checked ? 'small':'none')} />} 
                  label={`Reranker: ${reranker}`}
                />
              </Stack>
              
              <Button 
                sx={{ mt: 3 }} 
                variant="contained" 
                onClick={runProof}
                startIcon={<PlayArrowIcon />}
                size="large"
                fullWidth
              >
                Run Experiment
              </Button>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <InsightsIcon sx={{ color: 'secondary.main' }} />
                <Typography variant="h6" fontWeight={600}>Knowledge Base</Typography>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  label="Collection"
                  value={docCollection}
                  onChange={(e) => setDocCollection(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Title"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Content"
                  multiline
                  minRows={4}
                  value={docContent}
                  onChange={(e) => setDocContent(e.target.value)}
                />
                <TextField
                  label="Metadata (JSON)"
                  multiline
                  minRows={2}
                  value={docMetadata}
                  onChange={(e) => setDocMetadata(e.target.value)}
                  helperText="Optional JSON metadata"
                />
                {docError && (
                  <Typography variant="body2" color="error">{docError}</Typography>
                )}
                {docMessage && (
                  <Typography variant="body2" color="success.main">{docMessage}</Typography>
                )}
                <Button
                  variant="outlined"
                  onClick={addDocument}
                  disabled={!docContent.trim() || docLoading}
                >
                  {docLoading ? 'Saving…' : 'Store Document'}
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={7} sx={{ height: '100%', overflow: 'auto' }}>
          <Stack spacing={3} sx={{ pl: 1 }}>
            {/* Current Run Results */}
            {run && (() => {
              const metrics = run?.metrics || {};
              const totalCases = metrics.total_cases ?? metrics.cases ?? '—';
              const coverage = metrics.citation_coverage;
              const coverageLabel = formatPercent(coverage, 0);
              const coverageText = coverageLabel === '—' ? '—' : `${coverageLabel}%`;
              const passRate = metrics.pass_rate;
              const passRateLabel = formatPercent(passRate, 0);
              const passRateText = passRateLabel === '—' ? '—' : `${passRateLabel}%`;
              const latency = metrics.avg_latency_ms ?? '—';

              return (
                <Paper sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <CheckIcon sx={{ color: 'success.main' }} />
                    <Typography variant="h6" fontWeight={600}>Latest Results</Typography>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight={700} color="success.contrastText">
                          {totalCases}
                        </Typography>
                        <Typography variant="caption" color="success.contrastText">Cases</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight={700} color="info.contrastText">
                          {coverageText}
                        </Typography>
                        <Typography variant="caption" color="info.contrastText">Coverage</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight={700} color="primary.contrastText">
                          {passRateText}
                        </Typography>
                        <Typography variant="caption" color="primary.contrastText">Pass Rate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight={700} color="warning.contrastText">
                          {latency}ms
                        </Typography>
                        <Typography variant="caption" color="warning.contrastText">Latency</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })()}

            {/* Run History */}
            {runs.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <TimelineIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>Experiment History</Typography>
                </Stack>
                
                <Stack spacing={1}>
                  {runs.map((r) => {
                    const runMetrics = r?.metrics || {};
                    const passRateValue = typeof runMetrics.pass_rate === 'number' ? runMetrics.pass_rate : null;
                    const coverageValue = typeof runMetrics.citation_coverage === 'number' ? runMetrics.citation_coverage : null;
                    const passRateChip = passRateValue === null ? '—' : `${formatPercent(passRateValue, 0)}% pass`;
                    const coverageChip = coverageValue === null ? '—' : `${formatPercent(coverageValue, 0)}% cov`;
                    const config = r?.config || {};
                    const kValue = config.k ?? '—';
                    const hybridValue = config.hybrid === undefined ? '—' : String(config.hybrid);
                    const lambdaValue = config.mmr_lambda ?? '—';

                    return (
                      <Box key={r.id} sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight={500}>
                            {formatTime(r.created_at)}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Typography variant="caption" sx={{ 
                              bgcolor: 'success.main', 
                              color: 'success.contrastText',
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1 
                            }}>
                              {coverageChip}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              bgcolor: 'info.main', 
                              color: 'info.contrastText',
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1 
                            }}>
                              {passRateChip}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          k={kValue} • hybrid={hybridValue} • λ={lambdaValue}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            )}

            <Paper sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <InsightsIcon sx={{ color: 'info.main' }} />
                <Typography variant="h6" fontWeight={600}>Stored Documents</Typography>
              </Stack>

              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    label="Search"
                    value={docSearch}
                    onChange={(e) => setDocSearch(e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Rows"
                    select
                    value={docLimit}
                    size="small"
                    onChange={(e) => {
                      const newLimit = Number(e.target.value) || 10;
                      setDocLimit(newLimit);
                    }}
                    sx={{ minWidth: { xs: '100%', sm: 120 } }}
                  >
                    {[5, 10, 20, 50].map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                </Stack>

                {docError && (
                  <Alert severity="error">{docError}</Alert>
                )}
                {docMessage && (
                  <Alert severity="success">{docMessage}</Alert>
                )}

                {docLoadingList ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading documents…
                  </Typography>
                ) : documents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No documents found.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {documents.map((doc) => {
                      const content = doc?.content || '';
                      const preview = content.length > 240 ? `${content.slice(0, 240)}…` : content;
                      const metadata = doc?.metadata && typeof doc.metadata === 'object' ? doc.metadata : null;

                      return (
                        <Box key={doc.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {doc?.title || 'Untitled'} • {doc?.collection || ensureCollection(docCollection)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Created {formatDateTime(doc.created_at)}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              disabled={docLoadingList}
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 1 }}>
                            {preview || 'No content available.'}
                          </Typography>
                          {metadata && Object.keys(metadata).length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Metadata: {JSON.stringify(metadata)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Page {docTotal === 0 ? 0 : docPage + 1} of {docTotal === 0 ? 0 : Math.ceil(docTotal / docLimit)} • {docTotal} total
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={docPage === 0 || docLoadingList}
                      onClick={() => setDocPage((prev) => Math.max(prev - 1, 0))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={docLoadingList || (docPage + 1) * docLimit >= docTotal}
                      onClick={() => setDocPage((prev) => prev + 1)}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {/* Placeholder for empty state */}
            {!run && runs.length === 0 && (
              <Paper sx={{ p: 6, textAlign: 'center', opacity: 0.7 }}>
                <ScienceIcon sx={{ fontSize: 64, mb: 2, color: 'primary.main' }} />
                <Typography variant="h6" gutterBottom>Ready to Experiment</Typography>
                <Typography variant="body2">
                  Configure your evaluation suite and retrieval settings, then run an experiment to see results here.
                </Typography>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* History Sidepanel */}
      <Drawer
        anchor="right"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            bgcolor: 'background.default'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon />
              Recent Runs
            </Typography>
            <IconButton onClick={() => setHistoryOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {recentRuns.map((run) => {
              const passRateValue = typeof run?.metrics?.pass_rate === 'number' ? run.metrics.pass_rate : null;
              const avatarLabel = passRateValue === null ? '?' : `${Math.round(passRateValue * 100)}`;
              const avatarColor = classifyPassRate(passRateValue).replace('default', 'info');
              const totalCases = run?.metrics?.total_cases ?? run?.metrics?.cases ?? '—';
              const latency = run?.metrics?.avg_latency_ms ?? '—';

              return (
                <ListItem 
                  key={run.id} 
                  button 
                  onClick={() => {
                    loadRunDetails(run.id);
                    setHistoryOpen(false);
                  }}
                  sx={{ 
                    mb: 1, 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1 
                  }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ 
                      bgcolor: avatarColor === 'success' ? 'success.main' : avatarColor === 'warning' ? 'warning.main' : avatarColor === 'error' ? 'error.main' : 'info.main',
                      width: 32, 
                      height: 32 
                    }}>
                      {avatarLabel}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={`${totalCases} cases`} />
                        <Chip size="small" label={`${latency}ms`} />
                      </Stack>
                    }
                    secondary={formatTime(run.created_at)}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {/* Analysis Drawer */}
      <Drawer
        anchor="right"
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 600,
            bgcolor: 'background.default',
            borderLeft: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsightsIcon />
              Analysis Tools
            </Typography>
            <IconButton onClick={() => setAnalysisOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedRun && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Run Details
                </Typography>
                <Typography variant="body2">
                  Run ID: {selectedRun.id}
                </Typography>
                <Typography variant="body2">
                  Pass Rate: {(selectedRun.metrics?.pass_rate * 100 || 0).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
